import EssenceAnimatedCheckbox from '../../../../components/essence/checkbox/animated-checkbox';
import React, { useState } from 'react';
import { ContextExpenseUser } from '../../../../contexts/expense/expense-context';

interface Props {
	currency: string;
	totalAmount: number;
	circleParticipants: ContextExpenseUser[];
	onChange: (participants: ContextExpenseUser[]) => void;
	loggedInUserId: string;
}

export default function ParticipantsList({
	currency,
	totalAmount,
	circleParticipants,
	onChange,
	loggedInUserId,
}: Props) {
	const [participants, setParticipants] = useState<ContextExpenseUser[]>(
		circleParticipants.map(participant => ({
			...participant,
			amount: participant.amount,
		})),
	);
	const [isDirty, setIsDirty] = useState(false);

	const onToggle = (userId?: string) => {
		let updatedParticipants = participants.map(participant => {
			if (participant.id === userId) {
				return {
					...participant,
					isChecked: !participant.isChecked,
					amount: 0,
				};
			}
			return participant;
		});

		if (!isDirty) {
			const checkedUsers = updatedParticipants.filter(participant => participant.isChecked);
			const amountToDistribute = parseFloat((totalAmount / checkedUsers.length).toFixed(2));
			updatedParticipants = updatedParticipants.map(participant => ({
				...participant,
				amount: participant.isChecked ? amountToDistribute : 0,
			}));

			if (checkedUsers.length === participants.length) {
				const debitAmount = updatedParticipants.reduce(
					(sum, participant) => sum + Number(participant.amount),
					0,
				);
				const remainingAmount = totalAmount - debitAmount;
				if (remainingAmount !== 0) {
					const randomIndex = Math.floor(Math.random() * circleParticipants.length);
					updatedParticipants = updatedParticipants.map((participant, index) => {
						if (index === randomIndex) {
							return {
								...participant,
								amount: parseFloat((amountToDistribute + remainingAmount).toFixed(2)),
							};
						} else {
							return participant;
						}
					});
				}
			}
		}
		setParticipants(updatedParticipants);
		onChange(updatedParticipants);
	};

	const onAmountChange = ($event: React.ChangeEvent<HTMLInputElement>, userId?: string) => {
		setIsDirty(true);
		const regex = new RegExp(/^[0-9]*\.?[0-9]{0,2}?$/);
		const amount = $event.target.value;
		if (regex.test(amount)) {
			const updatedParticipants = participants.map(participant => {
				if (participant.id === userId) {
					return { ...participant, amount: amount };
				}
				return participant;
			});
			setParticipants(updatedParticipants);
			onChange(updatedParticipants);
		}
	};
	const onBlur = () => {
		const updatedParticipants = participants.map(participant => {
			if (participant.amount) {
				return {
					...participant,
					amount: participant.amount,
				};
			}
			return participant;
		});
		setParticipants(updatedParticipants);
	};

	const getName = (participant: ContextExpenseUser) => {
		let name = '-';
		if (participant.id === loggedInUserId) {
			name = 'You';
		} else if (participant.name) {
			name = participant.name;
		}
		return name;
	};

	const getPhoneNumber = (participant: ContextExpenseUser) => {
		let phoneNumber = '-';
		if (participant.phoneNumber) {
			phoneNumber = participant.phoneNumber;
		} else {
			return;
		}

		if (participant.countryCode) {
			return `+${participant.countryCode.toString()} ${phoneNumber}`;
		}
		return phoneNumber;
	};

	return (
		<>
			{participants.map(participant => (
				<li
					key={participant.id}
					style={{
						display: 'flex',
						flexDirection: 'row',
						justifyContent: 'space-between',
						alignItems: 'center',
					}}>
					<EssenceAnimatedCheckbox
						title={getName(participant)}
						subTitle={getPhoneNumber(participant)}
						checked={participant.isChecked}
						onChange={() => {
							onToggle(participant.id);
						}}
						testId={participant.id}
					/>
					<div className="flex flex-row gap-2 items-center justify-content-center">
						{participant.isChecked && <div className="headline-4">{currency}</div>}
						{participant.isChecked && (
							<input
								onBlur={onBlur}
								essence-input="true"
								value={participant.amount}
								onChange={e => {
									onAmountChange(e, participant.id);
								}}
								type="tel"></input>
						)}
					</div>
				</li>
			))}
		</>
	);
}
